import { Injectable } from "@angular/core";
import { Observable, firstValueFrom } from "rxjs";
import { LoginRequest } from "../domains/login-request";
import { LoginResponse } from "../domains/login-response";
import {
  IUserDashboardColumnsConfig,
  IUserAteColumnsConfig,
  User,
  IUserPbBeaconRptColumnsConfig,
} from "../domains/user";
import { Role } from "../domains/role";

import { CcpApi } from "../providers/api/ccp-api";
import { Account } from "../domains/account";

import * as moment from "moment-timezone";
import { IParimUpdateStatusPayload } from "../domains/employee";
import {
  ITAoutcome,
  ILog,
  IWarnType,
  IGeolocation,
  ISmsSignInConfirmation,
  ISmsSignInConfirmationResultToEmployee,
  ISmsSignInEarlyConfirmation,
  ISmsSignInEarlyConfirmationResultToEmployee,
  ISmsSignOutLateToEmployee,
  ISmsSignOutEarlyConfirmationResultToEmployee,
  ISmsSignOutLateConfirmation,
  ISmsSignOutEarlyConfirmation,
  //IManagerAskedToStayLate,
  ISmsSignOutConfirmation,
  ISmsSignOutConfirmationResultToEmployee,
  IReasonData,
  ISmsSignOutLateConfirmationResultToEmployee,
} from "../domains/iYa-bot-schema";
import { IPeriodTimestamps } from "../domains/time";
import { Note, NoteForPunchBuddyDasboard } from "../domains/note";
import { UserVenue, IVenue } from "../domains/venue";
import {
  ClientTimeScheduleRule,
  ClientTime,
  IClearMainShiftBody,
} from "../domains/crwClientTime";
import { HttpHeaders } from "@angular/common/http";
import { IOverwriteParimDataBody } from "../domains/overwriteShift";
import { WebsocketService } from "./websocket.service";
import { IPunchBuddyAction, IPunchBuddyNote } from "../domains/punchBuddy";

@Injectable({
  providedIn: "root",
})
export class CcpService {
  constructor(
    public api: CcpApi,
    private webSocket: WebsocketService
  ) {}

  // System
  getSettings(): Observable<any> {
    return this.api.http.get<any>(this.api.baseUrl + `/system/settings`);
  }

  getRolesMap(): Observable<any> {
    return this.api.http.get<any>(
      this.api.baseUrl + `/system/settings/rolesMap`
    );
  }

  getLocationsTime(): Observable<ClientTime[]> {
    return this.api.http.get<ClientTime[]>(
      this.api.baseUrl + `/system/settings/locationsTime`
    );
  }

  updateLocationTime(location: ClientTime): Observable<void> {
    console.log("updateLocationTime location", location);
    return this.api.http.put<void>(
      this.api.baseUrl + `/system/settings/locationTime`,
      location
    );
  }

  clearMainShiftForDay(body: IClearMainShiftBody): Observable<any> {
    return this.api.http.put<any>(
      this.api.baseUrl + `/system/settings/locationTime/clearMainShift`,
      body
    );
  }

  updateShiftScheduleTimeRule(rule: ClientTimeScheduleRule): Observable<void> {
    return this.api.http.put<void>(
      this.api.baseUrl + `/system/settings/clientTimeScheduleRule`,
      rule
    );
  }

  addShiftScheduleTimeRule(rule: ClientTimeScheduleRule): Observable<void> {
    return this.api.http.post<void>(
      this.api.baseUrl + `/system/settings/clientTimeScheduleRule`,
      rule
    );
  }

  deleteShiftScheduleTimeRule(id: number): Observable<void> {
    return this.api.http.delete<any>(
      this.api.baseUrl + `/system/settings/clientTimeScheduleRule/${id}`
    );
  }

  updateStartDate(data: any): Observable<void> {
    return this.api.http.put<void>(
      this.api.baseUrl + `/system/settings/startDate`,
      data
    );
  }

  updateEndDate(data: any): Observable<void> {
    return this.api.http.put<void>(
      this.api.baseUrl + `/system/settings/endDate`,
      data
    );
  }

  updateZone(data: any): Observable<any> {
    return this.api.http.post<any>(
      this.api.baseUrl + `/system/settings/zone`,
      data
    );
  }

  getLocationTimeChanges(location_uuid: string): Observable<any[]> {
    return this.api.http.get<any[]>(
      this.api.baseUrl + `/system/settings/locationTimeChanges/${location_uuid}`
    );
  }

  getAllLocationTimeChanges(): Observable<any[]> {
    return this.api.http.get<any[]>(
      this.api.baseUrl + `/system/settings/locationTimeChanges`
    );
  }
  //

  // Login
  login(loginRequest: LoginRequest): Observable<LoginResponse> {
    return this.api.http.post<LoginResponse>(
      this.api.baseUrl + "/users/login",
      loginRequest
    );
  }
  //

  // Parim Data
  getParimDataForCoordinator(
    fromDate: Date,
    accountIds: string,
    rangeType: string,
    isCheckingForNewRecords = false
  ): Observable<any[]> {
    console.log("getParimDataForCoordinator");
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let period: IPeriodTimestamps = this.getRange(fromDate, new Date());
    if (rangeType === "single_day") {
      period = this.getRange(fromDate, fromDate);
    }
    return isCheckingForNewRecords
      ? this.webSocket.sendMessageForRefreshDashboard(
          accountIds,
          period.startOfDayTimestamp,
          period.endOfDayTimestamp,
          1
        )
      : this.api.http.get<any[]>(
          this.api.baseUrl +
            `/parimApi/getParimDataForDashboard/${accountIds}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}/1?timezone=${timeZone}`
          // `/parimApi/getParimDataForDashboard/2/1696737600/1696824000/1`
        );
  }

  getParimDataForAuditor(
    fromDate: Date,
    accountIds: string,
    rangeType: string,
    isCheckingForNewRecords: boolean
  ): Observable<any[]> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let period: IPeriodTimestamps = this.getRange(fromDate, new Date());
    if (rangeType === "single_day") {
      period = this.getRange(fromDate, fromDate);
    }
    return isCheckingForNewRecords
      ? this.webSocket.sendMessageForRefreshDashboard(
          accountIds,
          period.startOfDayTimestamp,
          period.endOfDayTimestamp,
          2
        )
      : this.api.http.get<any[]>(
          this.api.baseUrl +
            `/parimApi/getParimDataForDashboard/${accountIds}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}/2?timezone=${timeZone}`
          // `/parimApi/getParimDataForDashboard/2/1696737600/1696824000/2`
        );
  }

  // returnUpdatedRow asks the server to send the changed row back, so the caller
  // can patch its own copy instead of refetching a whole report and losing the
  // filters (Story 761). It is a query param rather than a body field because the
  // body is tsoa-validated with additionalProperties=false. Default false, so
  // every existing caller behaves exactly as before.
  updateParimData(
    parimUpdateStatusPayload: IParimUpdateStatusPayload,
    returnUpdatedRow = false
  ): Observable<any> {
    const query = returnUpdatedRow ? "?returnUpdatedRow=true" : "";
    return this.api.http.put<any>(
      this.api.baseUrl + `/parimApi${query}`,
      parimUpdateStatusPayload
    );
  }

  updateParimDataBulk(
    parimUpdateStatusPayloads: IParimUpdateStatusPayload[]
  ): Observable<any[]> {
    return this.api.http.put<any[]>(
      this.api.baseUrl + `/parimApi/bulk`,
      parimUpdateStatusPayloads
    );
  }

  addNote(note: Note): Observable<void> {
    return this.api.http.post<void>(this.api.baseUrl + `/notes`, note);
  }

  addBulkNotes(notesData: Note[], files: File[]): Observable<any> {
    const formData = new FormData();
    formData.append("notesData", JSON.stringify(notesData));

    // Add files if present
    files.forEach((file, index) => {
      if (file) {
        formData.append(`file${index}`, file, file.name);
      }
    });

    return this.api.http.post<any>(this.api.baseUrl + `/notes/bulk`, formData);
  }

  addNoteForPunchBuddyDasboard(note: NoteForPunchBuddyDasboard) {
    return this.api.http.post<void>(
      this.api.baseUrl + `/notes/addNoteForPunchBuddyDasboard`,
      note
    );
  }

  addNoteFile(fileToUpload: any, noteId: number): Observable<any> {
    const formData: FormData = new FormData();
    formData.append("file", fileToUpload, fileToUpload.name);
    formData.append("noteId", noteId.toString());
    formData.append("fileName", fileToUpload.name);
    //let headers = new HttpHeaders();

    return this.api.http.post<void>(
      this.api.baseUrl + `/notes/noteFile`,
      formData
      //{ headers: headers }
    );
  }

  getEmployeeDetails(account_id: number, user_uuid: string): Observable<any> {
    return this.api.http.get<any>(
      `${this.api.baseUrl}/parimApi/getEmployeeInfo/${account_id}/${user_uuid}`
    );
  }

  getIYaBotChatHistory(parimDataId: number): Observable<any> {
    return this.api.http.get<any>(
      `${this.api.baseUrl}/parimApi/getIYaBotChatHistory/${parimDataId}`
    );
  }

  getEntryDetails(parimDataStateId: number): Observable<any> {
    //const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.api.http.get<any>(
      this.api.baseUrl + `/parimApi/getEntryDetails/${parimDataStateId}`
    );
  }

  getEntryDetailsByParimDataId(parimDataId: number): Observable<any> {
    return this.api.http.get<any>(
      this.api.baseUrl + `/parimApi/getEntryDetailsByParimDataId/${parimDataId}`
    );
  }
  //

  // Accounts
  getAllAccounts(): Observable<Account[]> {
    return this.api.http.get<Account[]>(this.api.baseUrl + "/accounts");
  }

  enqueueSyncShiftsMainFullSync(accountID: number): Observable<any> {
    return this.api.http.post<any>(
      this.api.baseUrl + "/parimApi/enqueueSyncShiftsMainFullSync",
      { accountID }
    );
  }
  //

  // Users
  getAllUsers(): Observable<User[]> {
    return this.api.http.get<User[]>(this.api.baseUrl + "/users");
  }

  createUser(user: User): Observable<void> {
    return this.api.http.post<void>(this.api.baseUrl + `/users`, user);
  }

  updateUser(user: User): Observable<void> {
    return this.api.http.put<void>(this.api.baseUrl + `/users`, user);
  }

  updateUserDashboardColumnsConfig(
    user: IUserDashboardColumnsConfig
  ): Observable<void> {
    return this.api.http.put<void>(this.api.baseUrl + `/users`, user);
  }

  updateUserAteColumnsConfig(user: IUserAteColumnsConfig): Observable<void> {
    return this.api.http.put<void>(this.api.baseUrl + `/users`, user);
  }

  updateUserPbBeaconRptColumnsConfig(
    user: IUserPbBeaconRptColumnsConfig
  ): Observable<void> {
    return this.api.http.put<void>(this.api.baseUrl + `/users`, user);
  }

  deleteUser(id: number): Observable<any> {
    return this.api.http.delete<any>(this.api.baseUrl + `/users/${id}`);
  }
  //

  // Roles
  getAllRoles(): Observable<Role[]> {
    return this.api.http.get<Role[]>(this.api.baseUrl + "/roles");
  }

  createRole(role: Role): Observable<void> {
    return this.api.http.post<void>(this.api.baseUrl + `/roles`, role);
  }

  updateRole(role: Role): Observable<void> {
    return this.api.http.put<void>(this.api.baseUrl + `/roles`, role);
  }

  deleteRole(id: number): Observable<any> {
    return this.api.http.delete<any>(this.api.baseUrl + `/roles/${id}`);
  }
  //

  // Venues
  getAllVenues(accountId: number): Observable<IVenue[]> {
    return this.api.http.get<IVenue[]>(
      `${this.api.baseUrl}/venues/getAll/${accountId}`
    );
  }

  updateVenues(userVenues: UserVenue): Observable<void> {
    return this.api.http.post<void>(
      this.api.baseUrl + `/users/updateVenues`,
      userVenues
    );
  }
  //

  // Reports
  getDataForReportByLocation(
    accountID: number,
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/byLocation/${accountID}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getDataForReportByEmployee(
    accountID: number,
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/byEmployee/${accountID}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getDataForReportByCategory(
    accountID: number,
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ) {
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any>(
      this.api.baseUrl +
        `/reports/byCategory/${accountID}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getDataForReportAllTimeEntries(
    accountIDs: number[],
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    console.log("getDataForReportAllTimeEntries");
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/allTimeEntries/${accountIDs}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getDataForPbDashboardFromReportService(
    accountID: number,
    dateFrom: Date | undefined,
    dateTo: Date | undefined,
    category: string
  ): Observable<any[]> {
    console.log("getDataForPbDashboard");
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/pbDashboardReport/${accountID}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}/${category}`
    );
  }

  getDataForReportForCoordinator(
    accountID: number,
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    console.log("getDataForReportForCoordinator");
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/forCoordinator/${accountID}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getParimDataByTickeNumber(ticketID: string): Observable<any[]> {
    return this.api.http.get<any[]>(
      this.api.baseUrl + `/reports/getParimDataByTickeNumber/${ticketID}`
    );
  }

  getEmployeesForAutocomplete(
    accountID: number,
    employeeName: string
  ): Observable<any[]> {
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/users/getEmployeesForAutocomplete/${accountID}/${employeeName}`
    );
  }

  getDataForPingReportByEmployee(
    employeeID: string,
    dateFrom: Date,
    dateTo: Date
  ) {
    const period = this.getRange(dateFrom, dateTo);

    return this.api.http.get<any[]>(
      `${this.api.baseUrl}/beWhere/getDataForPingReportByEmployee/${employeeID}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getDataForReportAnomaly(
    accountID: number,
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    console.log("getDataForReportAnomaly");
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/anomaly/${accountID}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getDataForCarrawayReport(
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    console.log("getDataForCarrawayReport");
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/carrawayReport/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getDataForReportAllTimeEntriesByEmloyee(
    accountID: number,
    employeeID: number,
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    console.log("getDataForReportAllTimeEntriesByEmployee");
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/allTimeEntriesByEmployee/${accountID}/${employeeID}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getDataForPayrollReport(
    accountID: number,
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    console.log("getDataForPayrollReport");
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/payroll/${accountID}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  // Story 811 - the payroll module's "enrolled in benefits, earned no wages"
  // view. Deliberately given the same range as getDataForPayrollReport above:
  // the server applies the payroll report's own window rules, so the two
  // always describe the same pay cycle.
  getBenefitsNoTimesheetReport(
    accountID: number,
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/benefitsNoTimesheet/${accountID}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  // Story 832 - the "owes for uniforms, earned no wages" view, on the same
  // range as the two above.
  getUniformOwingNoShiftsReport(
    accountID: number,
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/uniformOwingNoShifts/${accountID}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  // Story 841 - tags shown and edited from the benefits view. Core writes the
  // same tables thriveo-shopley-server owns, so a tag set here shows up there.
  getEmployeeTags(accountID: number): Observable<any[]> {
    return this.api.http.get<any[]>(
      this.api.baseUrl + `/employeeTags/account/${accountID}`
    );
  }

  bulkAddEmployeeTag(tagId: number, employeeIds: number[]): Observable<any> {
    return this.api.http.post<any>(
      this.api.baseUrl + `/employeeTags/bulk/add`,
      { tagId, employeeIds }
    );
  }

  bulkRemoveEmployeeTag(tagId: number, employeeIds: number[]): Observable<any> {
    return this.api.http.post<any>(
      this.api.baseUrl + `/employeeTags/bulk/remove`,
      { tagId, employeeIds }
    );
  }

  calculateOvertimeForShifts(
    accountID: number,
    payrollCycleId: number
  ): Observable<any> {
    return this.api.http.post<any>(
      this.api.baseUrl + `/parimApi/calculateOvertimeForShifts`,
      { accountID, payrollCycleId, forceRecalculate: true }
    );
  }

  calcPayrollFieldsForParimDatas(
    parimDataIds: number[],
    forceRecalculate = true
  ): Observable<any> {
    return this.api.http.post<any>(
      this.api.baseUrl + `/parimApi/calcPayrollFieldsForParimDatas`,
      { parimDataIds, forceRecalculate }
    );
  }

  recalculatePayroll(
    payrollCycleId: number,
    parimDataIds?: number[]
  ): Observable<any> {
    const body: { payrollCycleId: number; parimDataIds?: number[] } = {
      payrollCycleId,
    };
    if (parimDataIds?.length) {
      body.parimDataIds = parimDataIds;
    }
    return this.api.http.post<any>(
      this.api.baseUrl + `/parimApi/recalculatePayroll`,
      body
    );
  }

  getDataForIyaEmployeeEngagement(
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    console.log("getDataForIyaEmployeeEngagementReport");
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/iyaEmployeeEngagementReport/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getDataForPbBeaconReport(accountIDs: string): Observable<any[]> {
    console.log("getDataForPbBeaconReport");
    return this.api.http.get<any[]>(
      this.api.baseUrl + `/reports/pbBeaconReport/${accountIDs}`
    );
  }

  overwriteShift(body: IOverwriteParimDataBody): Observable<any> {
    return this.api.http.put<any[]>(
      this.api.baseUrl + `/parimApi/overwriteParimData`,
      body
    );
  }

  overwritePaymentInfo(body: {
    parimDataId: number;
    parimDataStateId: number;
    comment: string;
    rate?: number;
    actual_break_hours?: number;
    flat_rate?: number;
    flat_rate_hour_price?: number;
  }): Observable<any> {
    return this.api.http.put<any>(
      this.api.baseUrl + `/parimApi/overwritePaymentInfo`,
      body
    );
  }

  getDataForReportSBTC(
    accountIDs: string,
    dateFrom: Date | undefined,
    dateTo: Date | undefined
  ): Observable<any[]> {
    console.log("getDataForReportSBTC");
    const period = this.getRange(dateFrom, dateTo);
    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/reports/sbtcReport/${accountIDs}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getVenueRadiusForClocking(accountId: number): Observable<any[]> {
    return this.api.http.get<any[]>(
      this.api.baseUrl + `/venues/getVenueRadiusForClocking/${accountId}`
    );
  }

  getAbsences(
    accountIds: string,
    dateFrom: Date | null,
    dateTo: Date | null = null
  ): Observable<any[]> {
    const period = this.getRange(dateFrom!, dateTo || new Date());

    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/absences/getByDateRange/${accountIds}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getAbsenceDetails(absenceId: number): Observable<any> {
    return this.api.http.get<any>(this.api.baseUrl + `/absences/${absenceId}`);
  }

  updateAbsence(changes: any): Observable<any> {
    const body = {
      updates: changes,
    };

    return this.api.http.put<any>(this.api.baseUrl + `/absences/bulk`, body);
  }

  addAbsenceNote(note: any): Observable<any> {
    return this.api.http.post<any>(
      this.api.baseUrl + `/absences/addAbsenceNote`,
      note
    );
  }

  // Incoming shifts
  getIncomingShifts(accountIds: string): Observable<any[]> {
    console.log("getIncomingShifts");
    // period for the next 24 hours
    const now = moment();
    const startTimestamp = parseInt((now.valueOf() / 1000).toFixed());
    const endTimestamp = startTimestamp + 86400;

    return this.api.http.get<any[]>(
      this.api.baseUrl +
        `/parimApi/getIncomingShifts/${accountIds}/${startTimestamp}/${endTimestamp}`
      // `/parimApi/getParimDataForDashboard/2/1696737600/1696824000/1`
    );
  }

  //

  // iYa bot
  async checkIYaBotLink(type: string, parimDataId: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.api.http
        .get<boolean>(
          this.api.baseUrl + `/iYa/checkIYaBotLink/${type}/${parimDataId}`
        )
        .subscribe({
          next: async (response) => {
            console.log("checkIYaBotLink success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("checkIYaBotLink error", error);
            reject(error);
          },
        });
    });
  }

  async updateSignInPromise(data: ILog): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(this.api.baseUrl + `/iYa/updateClockInPromise`, data)
        .subscribe({
          next: async (response) => {
            console.log("updateSignInPromise success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("updateSignInPromise error", error);
            reject(error);
          },
        });
    });
  }

  async logAction(data: ILog): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http.put<void>(this.api.baseUrl + `/iYa/log`, data).subscribe({
        next: async (response) => {
          console.log("logAction success", response);

          resolve(response);
        },
        error: (error) => {
          console.log("logAction error", error);
          reject(error);
        },
      });
    });
  }

  async logBatteryAction(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(this.api.baseUrl + `/beWhere/log`, data)
        .subscribe({
          next: async (response) => {
            console.log("logBatteryAction success", response);

            resolve(response);
          },
          error: (error) => {
            console.log("logBatteryAction error", error);
            reject(error);
          },
        });
    });
  }

  async canNotMakeShift(id: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(this.api.baseUrl + `/iYa/canNotMakeShift/${id}`, {})
        .subscribe({
          next: async (response) => {
            console.log("canNotMakeShift success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("canNotMakeShift error", error);
            reject(error);
          },
        });
    });
  }

  async setTAoutcome(data: ITAoutcome): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(this.api.baseUrl + `/iYa/setTAoutcome`, data)
        .subscribe({
          next: async (response) => {
            console.log("SetTAoutcome success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("SetTAoutcome error", error);
            reject(error);
          },
        });
    });
  }

  async setReason(data: IReasonData): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(this.api.baseUrl + `/iYa/setReason`, data)
        .subscribe({
          next: async (response) => {
            console.log("SetReason success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("SetReason error", error);
            reject(error);
          },
        });
    });
  }

  async updateWarnType(data: IWarnType): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(this.api.baseUrl + `/iYa/updateWarnType`, data)
        .subscribe({
          next: async (response) => {
            console.log("updateWarnType success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("updateWarnType error", error);
            reject(error);
          },
        });
    });
  }

  async saveGeolocation(data: IGeolocation): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(this.api.baseUrl + `/iYa/saveGeolocation`, data)
        .subscribe({
          next: async (response) => {
            console.log("saveGeolocation success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("saveGeolocation error", error);
            reject(error);
          },
        });
    });
  }

  async getEmployeeManagers(parimDataId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .get<
          any[]
        >(this.api.baseUrl + `/parimApi/getEmployeeManagers/${parimDataId}`)
        .subscribe({
          next: async (response) => {
            console.log("getEmployeeManagers success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("getEmployeeManagers error", error);
            reject(error);
          },
        });
    });
  }

  async sendSignInConfirmationSms(data: ISmsSignInConfirmation): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(this.api.baseUrl + `/iYa/sendSignInConfirmationSms`, data)
        .subscribe({
          next: async (response) => {
            console.log("sendSignInConfirmationSms success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("sendSignInConfirmationSms error", error);
            reject(error);
          },
        });
    });
  }

  async sendSignInEarlyConfirmationSms(
    data: ISmsSignInEarlyConfirmation
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl + `/iYa/sendSignInEarlyConfirmationSms`,
          data
        )
        .subscribe({
          next: async (response) => {
            console.log("sendSignInEarlyConfirmationSms success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("sendSignInEarlyConfirmationSms error", error);
            reject(error);
          },
        });
    });
  }

  async sendSignInConfirmationSmsResultToEmployee(
    data: ISmsSignInConfirmationResultToEmployee
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl + `/iYa/sendSignInConfirmationSmsResultToEmployee`,
          data
        )
        .subscribe({
          next: async (response) => {
            console.log(
              "sendSignInConfirmationSmsResultToEmployee success",
              response
            );
            resolve(response);
          },
          error: (error) => {
            console.log(
              "sendSignInConfirmationSmsResultToEmployee error",
              error
            );
            reject(error);
          },
        });
    });
  }

  async sendSignOutConfirmationSmsResultToEmployee(
    data: ISmsSignOutConfirmationResultToEmployee
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl + `/iYa/sendSignOutConfirmationSmsResultToEmployee`,
          data
        )
        .subscribe({
          next: async (response) => {
            console.log(
              "sendSignOutConfirmationSmsResultToEmployee success",
              response
            );
            resolve(response);
          },
          error: (error) => {
            console.log(
              "sendSignOutConfirmationSmsResultToEmployee error",
              error
            );
            reject(error);
          },
        });
    });
  }

  async sendSignInEarlyConfirmationSmsResultToEmployee(
    data: ISmsSignInEarlyConfirmationResultToEmployee
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl +
            `/iYa/sendSignInEarlyConfirmationSmsResultToEmployee`,
          data
        )
        .subscribe({
          next: async (response) => {
            console.log(
              "sendSignInEarlyConfirmationSmsResultToEmployee success",
              response
            );
            resolve(response);
          },
          error: (error) => {
            console.log(
              "sendSignInEarlyConfirmationSmsResultToEmployee error",
              error
            );
            reject(error);
          },
        });
    });
  }

  getParimDataState(id: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .get<void>(this.api.baseUrl + `/beWhere/getParimDataState/${id}`)
        .subscribe({
          next: resolve,
          error: reject,
        });
    });
  }

  async sendSignOutEarlyConfirmationSms(
    data: ISmsSignOutEarlyConfirmation
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl + `/iYa/sendSignOutEarlyConfirmationSms`,
          data
        )
        .subscribe({
          next: async (response) => {
            console.log("sendSignOutEarlyConfirmationSms success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("sendSignOutEarlyConfirmationSms error", error);
            reject(error);
          },
        });
    });
  }

  async sendSignOutEarlyConfirmationSmsResultToEmployee(
    data: ISmsSignOutEarlyConfirmationResultToEmployee
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl +
            `/iYa/sendSignOutEarlyConfirmationSmsResultToEmployee`,
          data
        )
        .subscribe({
          next: async (response) => {
            console.log(
              "sendSignOutEarlyConfirmationSmsResultToEmployee success",
              response
            );
            resolve(response);
          },
          error: (error) => {
            console.log(
              "sendSignOutEarlyConfirmationSmsResultToEmployee error",
              error
            );
            reject(error);
          },
        });
    });
  }

  async sendSignOutAccidentallySMSToEmployeeIntroductory(
    parimDataId: number
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl +
            `/iYa/sendSignOutAccidentallySMSToEmployeeIntroductory/${parimDataId}`,
          {}
        )
        .subscribe({
          next: async (response) => {
            console.log(
              "sendSignOutAccidentallySMSToEmployeeIntroductory success",
              response
            );
            resolve(response);
          },
          error: (error) => {
            console.log(
              "sendSignOutAccidentallySMSToEmployeeIntroductory error",
              error
            );
            reject(error);
          },
        });
    });
  }

  // async sendSignOutLateSmsToEmployee(
  //   data: ISmsSignOutLateToEmployee
  // ): Promise<any> {
  //   return new Promise((resolve, reject) => {
  //     this.api.http
  //       .put<void>(this.api.baseUrl + `/iYa/sendSignOutLateSmsToEmployee`, data)
  //       .subscribe({
  //         next: async (response) => {
  //           console.log("sendSignOutLateSmsToEmployee success", response);
  //           resolve(response);
  //         },
  //         error: (error) => {
  //           console.log("sendSignOutLateSmsToEmployee error", error);
  //           reject(error);
  //         },
  //       });
  //   });
  // }

  async sendSignOutLateConfirmationSms(
    data: ISmsSignOutLateConfirmation
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl + `/iYa/sendSignOutLateConfirmationSms`,
          data
        )
        .subscribe({
          next: async (response) => {
            console.log("sendSignOutLateConfirmationSms success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("sendSignOutLateConfirmationSms error", error);
            reject(error);
          },
        });
    });
  }

  async sendSignOutLateConfirmationSmsResultToEmployee(
    data: ISmsSignOutLateConfirmationResultToEmployee
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl +
            `/iYa/sendSignOutLateConfirmationSmsResultToEmployee`,
          data
        )
        .subscribe({
          next: async (response) => {
            console.log(
              "sendSignOutLateConfirmationSmsResultToEmployee success",
              response
            );
            resolve(response);
          },
          error: (error) => {
            console.log(
              "sendSignOutLateConfirmationSmsResultToEmployee error",
              error
            );
            reject(error);
          },
        });
    });
  }

  async sendSignOutConfirmationSms(
    data: ISmsSignOutConfirmation
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(this.api.baseUrl + `/iYa/sendSignOutConfirmationSms`, data)
        .subscribe({
          next: async (response) => {
            console.log("sendSignOutConfirmationSms success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("sendSignOutConfirmationSms error", error);
            reject(error);
          },
        });
    });
  }

  async sendSignOutFTCOConfirmationSms(
    data: ISmsSignOutConfirmation
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl + `/iYa/sendSignOutFTCOConfirmationSms`,
          data
        )
        .subscribe({
          next: async (response) => {
            console.log("sendSignOutFTCOConfirmationSms success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("sendSignOutFTCOConfirmationSms error", error);
            reject(error);
          },
        });
    });
  }

  logActionOld(data: ILog): Observable<void> {
    return this.api.http.put<void>(this.api.baseUrl + `/iYa/log`, data);
  }

  SetTAoutcomeOld(data: ITAoutcome): Observable<void> {
    return this.api.http.put<void>(
      this.api.baseUrl + `/iYa/setTAoutcome`,
      data
    );
  }

  updateSignInPromiseOld(data: ILog): Observable<void> {
    return this.api.http.put<void>(
      this.api.baseUrl + `/iYa/updateClockInPromise`,
      data
    );
  }

  canNotMakeShiftOld(id: number): Observable<void> {
    return this.api.http.put<void>(
      this.api.baseUrl + `/iYa/canNotMakeShift/${id}`,
      {}
    );
  }

  async setSOEAccidentallyClockedOutSMSOpen(parimDataId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl +
            `/iYa/setSOEAccidentallyClockedOutSMSOpen/${parimDataId}`,
          {}
        )
        .subscribe({
          next: async (response) => {
            console.log(
              "setSOEAccidentallyClockedOutSMSOpen success",
              response
            );
            resolve(response);
          },
          error: (error) => {
            console.log("setSOEAccidentallyClockedOutSMSOpen error", error);
            reject(error);
          },
        });
    });
  }

  async setIsAccidentallyClockedOut(parimDataId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api.http
        .put<void>(
          this.api.baseUrl + `/iYa/setIsAccidentallyClockedOut/${parimDataId}`,
          {}
        )
        .subscribe({
          next: async (response) => {
            console.log("setIsAccidentallyClockedOut success", response);
            resolve(response);
          },
          error: (error) => {
            console.log("setIsAccidentallyClockedOut error", error);
            reject(error);
          },
        });
    });
  }

  getDataForPunchBuddyDasboard(
    accountId: number,
    category: string | null,
    dateFrom: Date,
    dateTo: Date
  ): Observable<any> {
    const period = this.getRange(dateFrom, dateTo);

    return this.api.http.get<any[]>(
      `${this.api.baseUrl}/beWhere/getDataForPunchBuddyDasboard/${accountId}/${category}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}`
    );
  }

  getPBDetailsForMDM(pb_id: string): Observable<any> {
    return this.api.http.get<any[]>(
      `${this.api.baseUrl}/punchBuddies/getPBDetailsForMDM/${pb_id}`
    );
  }

  addPunchBuddyMDMNote(note: IPunchBuddyNote): Observable<void> {
    return this.api.http.post<void>(
      this.api.baseUrl + `/punchBuddies/addPunchBuddyMDMNote`,
      note
    );
  }

  addPunchBuddyMDMNoteFile(fileToUpload: any, noteId: number): Observable<any> {
    const formData: FormData = new FormData();
    formData.append("file", fileToUpload, fileToUpload.name);
    formData.append("noteId", noteId.toString());
    formData.append("fileName", fileToUpload.name);

    return this.api.http.post<void>(
      this.api.baseUrl + `/punchBuddies/noteFile`,
      formData
    );
  }

  addPunchBuddyMDMActions(actions: IPunchBuddyAction[]): Observable<void> {
    return this.api.http.post<void>(
      this.api.baseUrl + `/punchBuddies/addPunchBuddyMDMActions`,
      actions
    );
  }

  getAllEmployeesByAccountIdWithNoPunchBuddies(
    account_id: number
  ): Observable<any> {
    return this.api.http.get<any[]>(
      `${this.api.baseUrl}/parimApi/getAllEmployeesByAccountIdWithNoPunchBuddies/${account_id}`
    );
  }

  getAllEmployeesByAccountId(account_id: number): Observable<any> {
    return this.api.http.get<any[]>(
      `${this.api.baseUrl}/parimApi/getAllEmployeesByAccountId/${account_id}`
    );
  }

  getVenueDepartments(employeeId: number, accountId: number): Observable<any> {
    return this.api.http.get<any[]>(
      `${this.api.baseUrl}/venues/getVenueDepartments/${employeeId}/${accountId}`
    );
  }

  updateMobileQAVenues(
    employeeId: number,
    deviceId: number,
    userId: number,
    venues: number[]
  ): Observable<any> {
    return this.api.http.put<any>(
      `${this.api.baseUrl}/venues/updateMobileQAVenues`,
      { employeeId, deviceId, userId, venues }
    );
  }

  getVenuesByIds(ids: number[]): Observable<any> {
    return this.api.http.post<any>(
      `${this.api.baseUrl}/venues/getVenuesByIds`,
      { ids }
    );
  }

  getMobileQaEmployees(accountId: number): Observable<any> {
    return this.api.http.get<any>(
      `${this.api.baseUrl}/venues/getMobileQaEmployees/${accountId}`
    );
  }

  getMobileQaReport(
    employeeIds: number[],
    dateFrom: Date,
    dateTo: Date
  ): Observable<any> {
    const period = this.getRange(dateFrom, dateTo);
    const employeeIdsString = employeeIds.join(",");
    const timeZone = moment.tz.guess();

    return this.api.http.get<any>(
      `${this.api.baseUrl}/venues/getMobileQaReport/${employeeIdsString}/${period.startOfDayTimestamp}/${period.endOfDayTimestamp}?timeZone=${timeZone}`
    );
  }

  refreshBeWhereData(
    beWhereId: string,
    parimDataId: number,
    pings: number,
    from: number,
    to: number,
    justGetPoints: boolean
  ): Observable<any> {
    return this.api.http.get<any>(
      `${this.api.baseUrl}/beWhere/refresh/${beWhereId}/${parimDataId}/${pings}/${from}/${to}/${justGetPoints}`
    );
  }

  getBeWherePings(
    beWhereId: string,
    from: number,
    to: number
  ): Observable<any> {
    return this.api.http.get<any>(
      `${this.api.baseUrl}/beWhere/pings/${beWhereId}/${from}/${to}`
    );
  }

  // setSOEAccidentallyClockedOutSMSOpen(id: number): Observable<void> {
  //   return this.api.http.put<void>(
  //     this.api.baseUrl + `/iYa/setSOEAccidentallyClockedOutSMSOpen/${id}`,
  //     {}
  //   );
  // }
  // end iYa bot

  // Venue sync
  syncVenueDetails(venueId: number): Observable<any> {
    return this.api.http.get<any>(
      `${this.api.baseUrl}/parimApi/getVenueDetails?venueIds=${venueId}`
    );
  }

  // private getCurrentDayStartAndEnd(timeZone: string) {
  //   const now = moment().tz(timeZone);
  //   const startOfDay = now.startOf("day");
  //   const startOfDayTimestamp = parseInt(
  //     (startOfDay.valueOf() / 1000).toFixed()
  //   );
  //   const endOfDayTimestamp = parseInt((startOfDayTimestamp + 86400).toFixed());

  //   return {
  //     startOfDayTimestamp,
  //     endOfDayTimestamp,
  //   };
  // }

  private getRange(
    dateFrom: Date | undefined,
    dateTo: Date | undefined
    // timeZone: string
  ) {
    console.log("In getRange");
    console.log(dateFrom);
    console.log(dateTo);

    if (dateTo === null) {
      dateTo = dateFrom;
    }
    // const startOfDay = moment(dateFrom).tz(timeZone).startOf("day");
    const startOfDay = moment(dateFrom).startOf("day");
    const startOfDayTimestamp = Math.floor(startOfDay.valueOf() / 1000);

    //const endOfDay = moment(dateTo).tz(timeZone).endOf("day");
    const endOfDay = moment(dateTo).endOf("day");

    let endOfDayTimestamp = Math.floor(endOfDay.valueOf() / 1000);

    if (startOfDayTimestamp === endOfDayTimestamp) {
      endOfDayTimestamp = startOfDayTimestamp + 86400;
    }

    console.log("In America/Toronto timezone");
    console.log(startOfDayTimestamp);
    console.log(
      moment(startOfDayTimestamp * 1000)
        .tz("America/Toronto")
        .format("M/DD/YYYY HH:mm:ss")
    );

    console.log("In America/Toronto timezone");
    console.log(endOfDayTimestamp);
    console.log(
      moment(endOfDayTimestamp * 1000)
        .tz("America/Toronto")
        .format("M/DD/YYYY HH:mm:ss")
    );

    return {
      startOfDayTimestamp,
      endOfDayTimestamp,
    };
  }

  // Void / un-void a single payroll entry (Story 758). The row is not deleted:
  // the flag is what makes the payroll report strike it through and leave it out
  // of the exports and the employee's totals.
  voidParimData(parimDataId: number, isVoided: boolean): Observable<any> {
    return this.api.http.put<any>(
      this.api.baseUrl + `/parimApi/voidParimData`,
      { parimDataId, isVoided }
    );
  }

  // Create manual payroll entry
  createParimDataManually(data: any): Observable<any> {
    return this.api.http.post<any>(
      this.api.baseUrl + "/parimApi/createParimDataManually",
      data
    );
  }

  // Get payroll cycles by account ID
  getPayrollCyclesByAccountId(accountId: number): Observable<any[]> {
    return this.api.http.get<any[]>(
      this.api.baseUrl + `/payroll-cycles/byAccountId/${accountId}`
    );
  }
}
